/** История сцены 3D (undo/redo), короткое хранение в localStorage. */

export const MODULE_3D_HISTORY_MAX = 40;
/** Срок хранения истории в localStorage. */
export const MODULE_3D_HISTORY_TTL_MS = 2 * 60 * 60 * 1000; // 2 часа

export const getModule3DHistoryKey = (moduleId) => `module-3d-history-v1:${moduleId}`;

const cloneJson = (value) => {
    try {
        return structuredClone(value);
    } catch {
        try {
            return JSON.parse(JSON.stringify(value));
        } catch {
            return null;
        }
    }
};

/** Только поза и ключи — без тяжёлого detailData / base64 (иначе ломается commit + PUT). */
const slimInstanceForHistory = (inst) => {
    if (!inst) return inst;
    return {
        instanceId: inst.instanceId,
        detailKey: inst.detailKey,
        localPosition: inst.localPosition
            ? {
                x: Number(inst.localPosition.x) || 0,
                y: Number(inst.localPosition.y) || 0,
                z: Number(inst.localPosition.z) || 0,
            }
            : {x: 0, y: 0, z: 0},
        axes: cloneJson(inst.axes) || inst.axes,
        referenceAxes: cloneJson(inst.referenceAxes) || inst.referenceAxes,
        localRotationAngles: cloneJson(inst.localRotationAngles) || inst.localRotationAngles,
        faceFlip: Boolean(inst.faceFlip),
        euler: cloneJson(inst.euler) || inst.euler,
        textureId: inst.textureId ?? null,
        textureUrl: inst.textureUrl ?? null,
        textureScaleMm: inst.textureScaleMm,
        textureRotationDeg: inst.textureRotationDeg,
        handleMaterialId: inst.handleMaterialId ?? null,
        pacedUrlId: inst.pacedUrlId ?? null,
        pacedDetailModuleId: inst.pacedDetailModuleId ?? null,
        fromImport: Boolean(inst.fromImport),
        hidden: Boolean(inst.hidden),
        // Минимальные ссылки на деталь для восстановления UI (без modelBase64).
        detail: inst.detail
            ? {
                id: inst.detail.id,
                compositionDetailId: inst.detail.compositionDetailId,
                name: inst.detail.name,
                position: inst.detail.position,
                length: inst.detail.length,
                width: inst.detail.width,
                height: inst.detail.height,
                source: inst.detail.source,
                kind: inst.detail.kind,
                pacedDetailModule: inst.detail.pacedDetailModule
                    ? {id: inst.detail.pacedDetailModule.id}
                    : null,
            }
            : null,
        detailData: inst.detailData
            ? {
                id: inst.detailData.id,
                compositionDetailId: inst.detailData.compositionDetailId,
                name: inst.detailData.name,
                position: inst.detailData.position,
                length: inst.detailData.length,
                width: inst.detailData.width,
                height: inst.detailData.height,
                source: inst.detailData.source,
                kind: inst.detailData.kind,
            }
            : null,
    };
};

/** Снимок для undo/redo (лёгкий, без тяжёлой геометрии). */
export const createHistorySnapshot = ({
    placedDetails,
    selectedInstanceId = null,
    pacedModuleIdByKey = {},
    clearedPacedKeys = [],
}) => ({
    at: Date.now(),
    selectedInstanceId: selectedInstanceId ?? null,
    pacedModuleIdByKey: {...(pacedModuleIdByKey || {})},
    clearedPacedKeys: Array.from(clearedPacedKeys || []),
    placedDetails: (Array.isArray(placedDetails) ? placedDetails : []).map(slimInstanceForHistory),
});

export const loadModule3DHistory = (moduleId) => {
    if (moduleId == null || moduleId === "") {
        return {past: [], future: []};
    }
    try {
        const raw = localStorage.getItem(getModule3DHistoryKey(moduleId));
        if (!raw) return {past: [], future: []};
        const parsed = JSON.parse(raw);
        const savedAt = Number(parsed?.savedAt) || 0;
        if (!savedAt || Date.now() - savedAt > MODULE_3D_HISTORY_TTL_MS) {
            localStorage.removeItem(getModule3DHistoryKey(moduleId));
            return {past: [], future: []};
        }
        return {
            past: Array.isArray(parsed.past) ? parsed.past : [],
            future: Array.isArray(parsed.future) ? parsed.future : [],
        };
    } catch {
        return {past: [], future: []};
    }
};

export const saveModule3DHistory = (moduleId, {past, future}) => {
    if (moduleId == null || moduleId === "") return;
    try {
        const payload = {
            savedAt: Date.now(),
            past: (past || []).slice(-MODULE_3D_HISTORY_MAX),
            future: (future || []).slice(0, MODULE_3D_HISTORY_MAX),
        };
        localStorage.setItem(getModule3DHistoryKey(moduleId), JSON.stringify(payload));
    } catch {
        // quota / private mode — историю в памяти всё равно держим
    }
};

export const clearModule3DHistory = (moduleId) => {
    if (moduleId == null || moduleId === "") return;
    try {
        localStorage.removeItem(getModule3DHistoryKey(moduleId));
    } catch {
        // ignore
    }
};

/**
 * Добавить снимок текущего состояния в past, очистить future.
 * Возвращает новый {past, future}.
 */
export const pushHistorySnapshot = (history, snapshot) => {
    const past = [...(history?.past || []), snapshot].slice(-MODULE_3D_HISTORY_MAX);
    return {past, future: []};
};

export const undoHistory = (history, currentSnapshot) => {
    const past = history?.past || [];
    if (!past.length) {
        return {history, restored: null};
    }
    const restored = past[past.length - 1];
    return {
        history: {
            past: past.slice(0, -1),
            future: [currentSnapshot, ...(history?.future || [])].slice(0, MODULE_3D_HISTORY_MAX),
        },
        restored,
    };
};

export const redoHistory = (history, currentSnapshot) => {
    const future = history?.future || [];
    if (!future.length) {
        return {history, restored: null};
    }
    const restored = future[0];
    return {
        history: {
            past: [...(history?.past || []), currentSnapshot].slice(-MODULE_3D_HISTORY_MAX),
            future: future.slice(1),
        },
        restored,
    };
};
